import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import SalesBlockedWarning from '../shared/SalesBlockedWarning';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  DataGrid, 
  gridClasses, 
  useGridApiRef,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
  Pagination,
  PaginationItem
} from '@mui/material';

// Orange dot indicator
const OrangeDot = styled('span')({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#ff8c00',
  marginRight: '6px'
});

// Custom pagination component with black theme
function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      variant="outlined"
      shape="rounded"
      size="small"
      page={page + 1}
      count={pageCount}
      renderItem={(props) => <PaginationItem {...props} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
      sx={{ 
        marginTop: '5px',
        display: 'flex',
        justifyContent: 'center',
        '& .MuiPaginationItem-root': {
          minWidth: '24px',
          height: '24px',
          fontSize: '0.75rem',
          color: '#000',
          borderColor: '#000',
          '&.Mui-selected': {
            backgroundColor: '#000',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#333',
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }
      }}
    />
  );
}

// Page size constant
const PAGE_SIZE = 100;

const MuiBonlivTable = ({ 
  items = [], 
  handleSave, 
  handleDelete, 
  status = '',
  userPermissions = { isManager: false } 
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const apiRef = useGridApiRef();
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  // Add state to track temporary input values
  const [tempInputValues, setTempInputValues] = useState({});
  
  // Process data directly with useMemo instead of useEffect + state
  const processedRows = useMemo(() => {
    if (!Array.isArray(items)) return [];
    
    // Filter items to ensure they have CODE_ART (required for Data Grid row id)
    // Also filter out malformed objects that might have been incorrectly structured
    return items
      .filter(item => {
        // Must be an object
        if (!item || typeof item !== 'object') {
          return false;
        }
        
        // Must have CODE_ART at root level for Data Grid to work
        // Reject items where CODE_ART is nested under numeric keys (malformed structure)
        if (!item.CODE_ART) {
          // Check if this looks like an array converted to object (has numeric keys)
          const hasNumericKeys = Object.keys(item).some(key => /^\d+$/.test(key));
          if (hasNumericKeys) {
            console.warn('Rejecting malformed item structure (array-like object):', item);
          }
          return false;
        }
        
        return true;
      });
  }, [items]);
  
  // More reliable focus tracking using DOM observation
  const gridRef = useRef(null);
  
  // Set up a mutation observer to track focus changes in the grid
  useEffect(() => {
    if (!gridRef.current) return;
    
    // Function to handle focus change detection
    const handleFocusChange = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'tabindex' && 
            document.activeElement && 
            document.activeElement.closest('.MuiDataGrid-row')) {
          
          const rowElement = document.activeElement.closest('.MuiDataGrid-row');
          if (rowElement && rowElement.dataset.id) {
            setFocusedRowId(rowElement.dataset.id);
          }
        }
      }
    };
    
    // Create a mutation observer to watch for focus changes
    const observer = new MutationObserver(handleFocusChange);
    
    // Start observing the grid for attribute changes
    observer.observe(gridRef.current, { 
      attributes: true, 
      subtree: true, 
      attributeFilter: ['tabindex'] 
    });
    
    // Initial focus detection
    setTimeout(() => {
      const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
      if (focusedElement) {
        const rowElement = focusedElement.closest('.MuiDataGrid-row');
        if (rowElement && rowElement.dataset.id) {
          setFocusedRowId(rowElement.dataset.id);
        }
      }
    }, 100);
    
    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);
  
  // Additional keyboard event listener for more reliable focus tracking
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        // Use setTimeout to ensure this runs after the grid's internal handlers
        setTimeout(() => {
          const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
          if (focusedElement) {
            const rowElement = focusedElement.closest('.MuiDataGrid-row');
            if (rowElement && rowElement.dataset.id) {
              setFocusedRowId(rowElement.dataset.id);
            }
          }
        }, 0);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  // Check for quantity differences
  const hasQuantityDifferences = (row) => {
    if (!row) return false;
    
    // Compare quantities at different stages
    return (row.QTE !== undefined && row.qte_prepare !== undefined && 
            row.QTE !== -1 && row.qte_prepare !== -1 && 
            row.QTE !== row.qte_prepare) ||
           (row.qte_prepare !== undefined && row.qte_controle !== undefined && 
            row.qte_prepare !== -1 && row.qte_controle !== -1 && 
            row.qte_prepare !== row.qte_controle) ||
           (row.qte_controle !== undefined && row.qte_liv !== undefined && 
            row.qte_controle !== -1 && row.qte_liv !== -1 && 
            row.qte_controle !== row.qte_liv);
  };
  
  // Determine if a cell is editable based on status and field
  const isCellEditable = (params) => {
    // If user is manager, everything is editable regardless of status
    if (userPermissions?.isManager) {
      return true;
    }
    
    // If status is VALIDE or TRANSFERE, nothing is editable
    if (status === 'VALIDE' || status === 'TRANSFERE') {
      return false;
    }
    
    const field = params.field;
    
    // Price and discount fields are always editable (except when restricted by status)
    const isPriceOrDiscount = ['PV_TTC', 'REMISE'].includes(field);
    if (isPriceOrDiscount) {
      return true;
    }
    
    // Quantity fields are editable based on status
    const isQuantityField = ['QTE', 'qte_prepare', 'qte_controle', 'qte_liv'].includes(field);
    if (isQuantityField) {
      if (!status) {
        return true; // If no status, all quantity fields are editable
      } else {
        // Only specific quantity field is editable based on status
        return (status === 'EN_PREPARATION' && field === 'qte_prepare') ||
               (status === 'PREPARE' && field === 'qte_controle') ||
               (status === 'VALIDE' && field === 'qte_liv');
      }
    }
    
    return false;
  };
  
  // Modified processRowUpdate to handle string inputs
  const processRowUpdate = (newRow, oldRow) => {
    try {
      // Convert values to numbers only when saving
      const numericFields = ['QTE', 'qte_prepare', 'qte_controle', 'qte_liv', 'PV_TTC', 'REMISE'];
      const processedRow = { ...newRow };

      numericFields.forEach(field => {
        if (field in processedRow && processedRow[field] !== -1) {
          // Handle empty strings and invalid inputs
          if (processedRow[field] === '' || processedRow[field] === null) {
            if (field === 'REMISE') {
              processedRow[field] = 0;
            } else if (field === 'PV_TTC') {
              processedRow[field] = 0;
            } else {
              processedRow[field] = 0;
            }
          } else {
            // Convert string to number and handle NaN
            const numValue = Number(processedRow[field].toString().replace(',', '.'));
            processedRow[field] = isNaN(numValue) ? 0 : numValue;
          }
        }
      });

      // If PV_TTC was changed, recalculate PV_HT and set REMISE to 0
      if (oldRow.PV_TTC !== processedRow.PV_TTC) {
        const newPvTtc = Number(processedRow.PV_TTC);
        const tva = Number(processedRow.TVA) || 0;
        const newPvHt = newPvTtc / (1 + tva / 100);

        processedRow.REMISE = 0;
        processedRow.PV_HT = Math.round(newPvHt * 100) / 100;
      }

      // Additional validation for price and discount
      if ('PV_TTC' in processedRow) {
        processedRow.PV_TTC = Math.max(0, processedRow.PV_TTC);
      }

      if ('REMISE' in processedRow) {
        processedRow.REMISE = Math.min(100, Math.max(0, processedRow.REMISE));
      }

      // Clear temporary input value after saving
      setTempInputValues(prev => {
        const newValues = { ...prev };
        delete newValues[`${newRow.CODE_ART}-${Object.keys(newRow).find(key => newRow[key] !== oldRow[key])}`];
        return newValues;
      });

      // Notify parent handler
      handleSave(processedRow);
      return processedRow;
    } catch (error) {
      console.error('Error in processRowUpdate:', error);
      return oldRow;
    }
  };

  // Add handler for cell value changes
  const handleCellValueChange = (params) => {
    const { id, field, value } = params;
    setTempInputValues(prev => ({
      ...prev,
      [`${id}-${field}`]: value
    }));
  };
  
  // Helper function to check if a quantity field is editable
  const isQuantityFieldEditable = (field) => {
    if (userPermissions?.isManager) {
      return true;
    }
    
    if (status === 'VALIDE' || status === 'TRANSFERE') {
      return false;
    }
    
    if (!status) {
      return true;
    }
    
    return (status === 'EN_PREPARATION' && field === 'qte_prepare') ||
           (status === 'PREPARE' && field === 'qte_controle') ||
           (status === 'VALIDE' && field === 'qte_liv') ||
           (field === 'QTE' && !status);
  };
  
  // Column definitions
  const columns = [
    {
      field: 'CODE_ART',
      headerName: 'Code Art',
      width: 120,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasQuantityDifferences(params.row) && <OrangeDot />}
            {params.value || ''}
          </Box>
        );
      }
    },
    {
      field: 'ARTICLE',
      headerName: 'Article',
      flex: 1,
      renderCell: (params) => <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}><span title={params.value || ''} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.value || ''}</span><SalesBlockedWarning item={params.row} /></div>,
    },
    {
      field: 'qte_stk',
      headerName: 'Stock',
      width: 70,
      renderCell: (params) => {
        // More robust check before attempting to access article properties
        if (!params || !params.row) return 0;
        
        // Try to get stock from stocks array first (filtered by CODE_ENT), then fallback
        const stock = params.row?.article?.stocks?.[0];
        if (stock) {
          return stock.qte_stk || 0;
        }
        
        // Fallback to direct article properties
        if (params.row.article && typeof params.row.article === 'object') {
          return params.row.article.qte_stk || 0;
        }
        
        return params.row.qte_stk || 0;
      }
    },
    {
      field: 'QTE',
      headerName: 'Qté CMD',
      width: 80,
      editable: true,
      cellClassName: (params) => {
        if (!params || !params.row || params.value === -1) return '';
        
        return isQuantityFieldEditable('QTE') ? 'editable-cell' : '';
      },
      renderCell: (params) => {
        if (!params || !params.row || params.value === -1) return "N/A";
        return params.value;
      }
    },
    {
      field: 'qte_prepare',
      headerName: 'Qté Prép.',
      width: 80,
      editable: true,
      cellClassName: (params) => {
        if (!params || !params.row || params.value === -1) return '';
        
        const row = params.row;
        const shouldHighlight = (row.QTE !== undefined && row.qte_prepare !== undefined &&
                                row.QTE !== -1 && row.qte_prepare !== -1 && 
                                row.QTE !== row.qte_prepare);
        
        const isEditable = isQuantityFieldEditable('qte_prepare');
        
        if (shouldHighlight) {
          return isEditable ? 'highlighted-cell editable-cell' : 'highlighted-cell';
        }
        
        return isEditable ? 'editable-cell' : '';
      },
      renderCell: (params) => {
        if (!params || !params.row || params.value === -1) return "N/A";
        return params.value;
      }
    },
    {
      field: 'qte_controle',
      headerName: 'Qté Ctrl',
      width: 80,
      editable: true,
      cellClassName: (params) => {
        if (!params || !params.row || params.value === -1) return '';
        
        const row = params.row;
        const shouldHighlight = (row.qte_prepare !== undefined && row.qte_controle !== undefined &&
                                row.qte_prepare !== -1 && row.qte_controle !== -1 && 
                                row.qte_prepare !== row.qte_controle);
        
        const isEditable = isQuantityFieldEditable('qte_controle');
        
        if (shouldHighlight) {
          return isEditable ? 'highlighted-cell editable-cell' : 'highlighted-cell';
        }
        
        return isEditable ? 'editable-cell' : '';
      },
      renderCell: (params) => {
        if (!params || !params.row || params.value === -1) return "N/A";
        return params.value;
      }
    },
    {
      field: 'qte_liv',
      headerName: 'Qté Liv.',
      width: 80,
      editable: true,
      cellClassName: (params) => {
        if (!params || !params.row || params.value === -1) return '';
        
        const row = params.row;
        const shouldHighlight = (row.qte_controle !== undefined && row.qte_liv !== undefined &&
                                row.qte_controle !== -1 && row.qte_liv !== -1 && 
                                row.qte_controle !== row.qte_liv);
        
        const isEditable = isQuantityFieldEditable('qte_liv');
        
        if (shouldHighlight) {
          return isEditable ? 'highlighted-cell editable-cell' : 'highlighted-cell';
        }
        
        return isEditable ? 'editable-cell' : '';
      },
      renderCell: (params) => {
        if (!params || !params.row || params.value === -1) return "N/A";
        return params.value;
      }
    },
    {
      field: 'PV_TTC',
      headerName: 'Prix TTC',
      width: 80,
      editable: true,
      type: 'number',
      cellClassName: 'price-cell',
      valueParser: (value) => {
        // Convert input value to a valid number
        const parsedValue = parseFloat(value.toString().replace(',', '.'));
        return isNaN(parsedValue) ? 0 : parsedValue;
      },
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return '';
        
        // Try to convert to number and format
        try {
          const numValue = Number(params.value);
          return isNaN(numValue) ? '0.00' : `${numValue.toFixed(2)}`;
        } catch (error) {
          return '0.00';
        }
      }
    },
    {
      field: 'REMISE',
      headerName: '%R',
      width: 80,
      editable: true,
      type: 'number',
      cellClassName: 'price-cell',
      valueParser: (value) => {
        // Convert input value to a valid number
        const parsedValue = parseFloat(value.toString().replace(',', '.'));
        return isNaN(parsedValue) ? 0 : Math.min(100, Math.max(0, parsedValue));
      },
      renderCell: (params) => {
        if (params.value === undefined || params.value === null) return '';
        
        // Try to convert to number and format
        try {
          const numValue = Number(params.value);
          return isNaN(numValue) ? '0.00%' : `${numValue.toFixed(2)}%`;
        } catch (error) {
          return '0.00%';
        }
      }
    },
    {
      field: 'TotalTTC',
      headerName: 'Montant',
      width: 100,
      renderCell: (params) => {
        if (!params || !params.row) return '';
        
        const row = params.row;
        let quantity;
        
        switch (status) {
          case 'EN_PREPARATION':
            quantity = row.qte_prepare;
            break;
          case 'PREPARE':
            quantity = row.qte_controle;
            break;
          case 'VALIDE':
          case 'TRANSFERE':
            quantity = row.qte_liv;
            break;
          default:
            quantity = row.QTE;
        }
        
        // Check if quantity is valid
        if (quantity === -1 || quantity === undefined || quantity === null) {
          return 'N/A';
        }
        
        // Ensure both price and discount are valid numbers
        const price = row.PV_TTC !== undefined && row.PV_TTC !== null ? Number(row.PV_TTC) : 0;
        const discount = row.REMISE !== undefined && row.REMISE !== null ? Number(row.REMISE) : 0;
        
        // Calculate the total amount
        const totalAmount = Number(quantity) * price * (1 - discount / 100);
        
        // Format the result
        return isNaN(totalAmount) ? 'N/A' : `${totalAmount.toFixed(2)}`;
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        const canDelete = currentCan('deliveryNotes', 'update') && ((status !== 'VALIDE' && status !== 'TRANSFERE') || userPermissions?.isManager);
        
        return canDelete ? (
          <div 
            onClick={() => setConfirmDelete(params.row.CODE_ART)}
            style={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}
          >
            <span style={{ 
              fontSize: '14px', 
              letterSpacing: '2px',
              fontWeight: 'bold'
            }}>...</span>
          </div>
        ) : null;
      }
    }
  ];

  return (
    <Box sx={{ 
      width: '100%',
      '& .highlighted-cell': {
        color: '#ff8c00',
      },
      '& .editable-cell': {
        backgroundColor: 'rgba(25, 118, 210, 0.12)',
      },
      '& .price-cell': {
        color: '#1976d2',
      },
      '& .quantity-difference-row': {
        backgroundColor: 'rgba(255, 140, 0, 0.05)',
      },
      // Add styling for the focused row
      '& .focused-row': {
        borderTop: '2px solid black',
        borderBottom: '2px solid black',
      }
    }}>
      <DataGrid
        ref={gridRef}
        apiRef={apiRef}
        rows={processedRows}
        columns={columns}
        getRowId={(row) => row.CODE_ART}
        rowHeight={25}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Error updating row:', error)}
        isCellEditable={isCellEditable}
        disableColumnMenu
        
        // Add handlers for cell value changes and navigation
        onCellValueChange={handleCellValueChange}
        onCellFocusOut={(params) => {
          const { id, field } = params;
          const tempValue = tempInputValues[`${id}-${field}`];
          if (tempValue !== undefined) {
            const row = processedRows.find(r => r.CODE_ART === id);
            if (row) {
              const updatedRow = { ...row, [field]: tempValue };
              processRowUpdate(updatedRow, row);
            }
          }
        }}
        
        // Direct focus tracking without timeouts
        onCellClick={(params) => {
          setFocusedRowId(params.id);
        }}
        
        // We'll keep these for redundancy, but our DOM observation should handle most cases
        onRowClick={(params) => {
          setFocusedRowId(params.id);
        }}
        
        // Fallback for cell focus
        onCellFocusIn={(params) => {
          setFocusedRowId(params.id);
        }}
        
        // Enable pagination with custom component
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[PAGE_SIZE]}
        slots={{
          pagination: CustomPagination,
        }}
        
        // Keep fixed height for scrollable container within each page
        autoHeight={false}
        sx={{
          height: 800, // Fixed height to enable scrolling
          maxHeight: '65vh', // Alternative: use viewport height
          overflowY: 'auto',
          [`& .${gridClasses.cell}`]: {
            fontSize: '0.8rem',
            padding: '1px 8px',
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: 'none',
            border: 'solid #000000 1px',
            borderRadius: '4px',
          },
          [`& .${gridClasses.row}:hover`]: {
            backgroundColor: 'rgba(115, 127, 139, 0.08)',
            cursor: 'pointer',
          },
          [`& .${gridClasses.row}.Mui-selected`]: {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(115, 127, 139, 0.12)',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            border: 'none',
          }
        }}
        
        initialState={{
          sorting: {
            sortModel: [{ field: 'CODE_ART', sort: 'asc' }],
          },
        }}
        
        // Update getRowClassName to apply the focused-row class
        getRowClassName={(params) => {
          const classes = [];
          
          if (hasQuantityDifferences(params.row)) {
            classes.push('quantity-difference-row');
          }
          
          if (params.id === focusedRowId) {
            classes.push('focused-row');
          }
          
          return classes.join(' ');
        }}
        
        // Modify componentsProps to handle keyboard navigation
        componentsProps={{
          cell: {
            onKeyDown: (e) => {
              // Allow arrow keys and other navigation keys without preventing default
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
                return;
              }
              // For other keys, start edit mode
              if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
                e.defaultMuiPrevented = true;
              }
            }
          }
        }}
        
        // Make it easier to enter edit mode
        editMode="cell"
        
        // Keyboard navigation settings
        disableMultipleColumnsSorting
        disableMultipleSelection
        disableSelectionOnClick={false}
      />
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cet article?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PermissionButton   permissionLibrary="mui" onClick={() => setConfirmDelete(null)}>
            Non
          </PermissionButton>
          <PermissionButton permissionModule="deliveryNotes" permissionAction="update" permissionLibrary="mui"
            onClick={() => {
              handleDelete(confirmDelete);
              setConfirmDelete(null);
            }} 
            color="error" 
            autoFocus
          >
            Oui
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MuiBonlivTable;
